import { useCallback, useEffect, useRef, useState } from "react";
import { useGetPropertiesQuery } from "./propertiesApiSlice";
import Property from "./Property";
import {
  Box,
  CircularProgress,
  Container,
  Grid2,
  Typography,
} from "@mui/material";

const PropertiesList = () => {
  const [page, setPage] = useState(1);
  const [properties, setProperties] = useState([]);

  const { data, isFetching, isError, error, isSuccess } = useGetPropertiesQuery(
    page,
    {
      skip: !page, // Avoid re-fetching on initial mount
    }
  );
  const hasMore = data?.hasMore;

  // Update the properties list when data changes
  useEffect(() => {
    if (data && data.properties) {
      if (page === 1) {
        // On first page or when the filter changes, replace the list
        setProperties(data.properties);
      } else {
        // For subsequent pages, append the new data
        setProperties((prev) => [...prev, ...data.properties]);
      }
    }
  }, [data]);

  // Setup Intersection Observer for infinite scrolling
  const observer = useRef();
  const lastPropertyRef = useCallback(
    (node) => {
      if (isFetching) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1); // Trigger the next page load
        }
      });

      if (node) observer.current.observe(node);
    },
    [isFetching, hasMore]
  );

  let content;

  if (isFetching)
    content = (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );

  if (isError) {
    content = (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 10,
        }}
      >
        <Typography variant="subtitle1" component="p">
          {error?.data?.message}
        </Typography>
      </Box>
    );
  }

  if (isSuccess) {
    const propertyData = properties.length
      ? properties.map((property, index) => (
          <Property
            key={property.id}
            property={property}
            lastPropertyRef={
              index === properties.length - 1 ? lastPropertyRef : null
            }
          />
        ))
      : null;
    content = (
      <Container maxWidth="xl" sx={{ marginY: 5 }}>
        <Grid2 container spacing={3}>
          {propertyData}
        </Grid2>
      </Container>
    );
  }
  return content;
};

export default PropertiesList;
